/**
 * Downscale a PNG/JPG data URL so the resulting image is at most `maxWidth`
 * pixels wide while preserving aspect ratio. Keeps the original alpha
 * channel — important for signatures with transparent backgrounds.
 *
 * Returns the original URL untouched if the source is already smaller than
 * `maxWidth`, so re-saving an already-small signature doesn't lose fidelity
 * to a JPEG round-trip.
 */
export async function downscaleDataUrl(
  dataUrl: string,
  maxWidth = 600,
): Promise<string> {
  if (!dataUrl || !dataUrl.startsWith('data:image/')) return dataUrl;

  const img = await loadImage(dataUrl);
  if (img.width <= maxWidth) return dataUrl;

  const scale = maxWidth / img.width;
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);

  return canvas.toDataURL('image/png');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}
